/**
 * The one scroll animation the app actually uses: a row settling in as it comes into view.
 *
 * ## `from`, never `to`
 *
 * The tween animates *from* a hidden state to whatever CSS already says. The tempting
 * alternative — hide the cards in the stylesheet and animate *to* visible — has a failure
 * mode this app cannot accept: if the GSAP chunk fails to load, or the browser is old, or
 * motion is switched off after the class was applied, the content stays invisible and the
 * library looks empty. `from` degrades to "everything is simply there", which is the right
 * answer for every one of those cases.
 *
 * ## Refresh order, and why nothing sets `refreshPriority`
 *
 * ScrollTrigger refreshes in creation order, and these are created asynchronously — each
 * row waits on the shared GSAP promise. In practice the order holds, because React runs
 * effects top-to-bottom and the promise is memoised, so the callbacks resolve in the order
 * they were registered. It is safe to rely on that here for a stronger reason: these
 * triggers only fade and translate, so their refresh order cannot affect layout. The rule
 * exists for pinned sections, where pin spacing measured in the wrong order shifts
 * everything below it.
 *
 * Add `refreshPriority` here the day anything in this app pins.
 *
 * ## `once`
 *
 * The trigger fires a single time and retires. A row that re-animates every time it is
 * scrolled past is a page that never settles, and on a long library screen it is also a
 * ScrollTrigger doing work forever for an effect nobody is still looking at.
 */
import { useEffect, type RefObject } from 'react';
import { useGsap } from './useGsap';
import { loadMotionRuntime, motionRuntimeLoaded } from './runtime';

export interface ScrollRevealOptions {
  /** Elements to stagger in, resolved within `scope`. Defaults to the scope itself. */
  selector?: string;
  /** Seconds between each element. */
  stagger?: number;
  /** How far up the element travels, in pixels. */
  distance?: number;
  /** Re-runs the reveal — pass the row's item count so a late-arriving fetch is covered. */
  key?: unknown;
}

export function useScrollReveal(
  scope: RefObject<HTMLElement | null>,
  { selector, stagger = 0.045, distance = 18, key }: ScrollRevealOptions = {},
): void {
  useGsap(
    ({ gsap }) => {
      const root = scope.current;
      if (!root) return;

      const targets = selector ? gsap.utils.toArray<HTMLElement>(selector) : [root];
      if (targets.length === 0) return;

      gsap.from(targets, {
        opacity: 0,
        y: distance,
        duration: 0.45,
        ease: 'power2.out',
        stagger,
        scrollTrigger: {
          trigger: root,
          // Slightly inside the viewport rather than at its edge: triggering exactly at
          // the fold means the animation has already finished by the time the row is
          // properly on screen, so nobody sees it.
          start: 'top 88%',
          once: true,
        },
      });
    },
    [key],
    scope,
  );

  /*
   * Posters arrive after the row does, and each one that decodes changes the row's height
   * — which moves every trigger below it. ScrollTrigger caches those positions at creation,
   * so without this the rows further down the page fire at the wrong scroll offset, or
   * never.
   */
  useEffect(() => {
    const root = scope.current;
    if (!root) return;

    let frame = 0;
    const onLoad = () => {
      // Coalesced: twenty posters finishing in the same burst should cause one refresh,
      // and a refresh re-measures every trigger on the page.
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        // Never *causes* the engine to load — only asks an already-running one to
        // re-measure. Otherwise a device that declined motion would fetch GSAP the moment
        // an image finished decoding. Imported statically because `runtime.ts` is only
        // the loader: the code split it guards lives in the `import('gsap')` inside it,
        // and importing this module both ways made Rollup give up on splitting either.
        if (!motionRuntimeLoaded()) return;
        void loadMotionRuntime().then((rt) => rt.ScrollTrigger.refresh());
      });
    };

    root.addEventListener('load', onLoad, true); // capture: `load` on <img> does not bubble
    return () => {
      window.cancelAnimationFrame(frame);
      root.removeEventListener('load', onLoad, true);
    };
  }, [scope, key]);
}
